import { BusinessOutlined, EventOutlined, VpnKeyOutlined } from "@mui/icons-material";
import { Box, Stack, TextField } from "@mui/material";
import { COLORS } from "assets";
import { FormModalActions, FormRowSwitch, MyModal, SiteSelect, StyledInputLabel, TextInput } from "components";
import { ENDPOINTS } from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { useSnackbar } from "notistack";
import { FormEventHandler, useCallback, useEffect, useMemo, useState } from "react";
import { MILLISECONDS } from "types";
import { validateName } from "utils";
import { dateFieldSx, fieldSx, iconSx } from "./formShared";

const initState = {
  name: "",
  siteId: "",
  expiry: new Date(Date.now() + MILLISECONDS.YEAR).toISOString().substring(0, 10)
};
const defaultState =
  process.env.NODE_ENV === "development"
    ? ({
        ...initState,
        name: "Test APIToken"
      } as typeof initState)
    : initState;

interface IAdd {
  onSuccess?: () => void;
  onClose: () => void;
}

export default function Add({ onSuccess, onClose }: IAdd) {
  const [noExpiry, setNoExpiry] = useState(false);
  const [state, setState] = useState(defaultState);
  const [errors, setErrors] = useState({ name: "", expiry: "" });
  const t = useTranslation();
  const { isLoading, response, error, callFetch: callFetchPost } = useFetch<{ _id: string }>("POST");
  const { enqueueSnackbar } = useSnackbar();

  const disabledSubmit = useMemo(
    () => !!(errors.name || !state.name || !state.siteId || (!noExpiry && (!state.expiry || !!errors.expiry))),
    [errors, state, noExpiry]
  );

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      callFetchPost(ENDPOINTS.APIToken, {
        body: JSON.stringify({
          ...state,
          expiry: noExpiry ? null : state.expiry
        })
      }).catch(console.error);
    },
    [state, noExpiry, callFetchPost]
  );

  useEffect(() => {
    if (response) {
      enqueueSnackbar(response.message || "", { variant: "success" });
      onSuccess?.();
      onClose();
      setState(initState);
      setNoExpiry(false);
    }
  }, [response, onClose, enqueueSnackbar, onSuccess]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message || "", { variant: "error" });
      if (error.data?.errors) {
        setErrors((prevState) => ({
          ...prevState,
          ...error.data.errors
        }));
      }
    }
  }, [error, enqueueSnackbar]);

  return (
    <MyModal
      title={t["Add API Token"]}
      subtitle={t["Add API Token description"]}
      open={true}
      setOpen={onClose}
      variant="light"
    >
      <Box component="form" onSubmit={onSubmit}>
        <Stack spacing={2.5}>
          <TextInput
            value={state.name}
            label={t["Name"]}
            type="text"
            placeholder={t["e.g API for Backend"]}
            error={!!errors.name}
            helperText={errors.name}
            startAdornment={<VpnKeyOutlined sx={iconSx} />}
            sx={fieldSx}
            onChange={(e) => {
              setState((prevState) => ({
                ...prevState,
                name: e.target.value
              }));
              setErrors((prevState) => ({
                ...prevState,
                name: validateName(e.target.value)
              }));
            }}
            fullWidth
            required
            autoFocus
          />

          <SiteSelect
            adminOnly
            value={state.siteId || undefined}
            startAdornment={<BusinessOutlined sx={iconSx} />}
            onChange={(siteId) => setState((prevState) => ({ ...prevState, siteId }))}
          />

          <Box>
            <StyledInputLabel>{t["Expiry Date"]}</StyledInputLabel>
            <TextField
              required={!noExpiry}
              disabled={noExpiry}
              value={state.expiry}
              type="date"
              placeholder="2025-10-10"
              error={!!errors.expiry}
              helperText={errors.expiry || " "}
              onChange={(e) => {
                setState((prevState) => ({
                  ...prevState,
                  expiry: e.target.value
                }));
                setErrors((prevState) => ({
                  ...prevState,
                  expiry: ""
                }));
              }}
              inputProps={{
                min: new Date(Date.now()).toISOString().substring(0, 10)
              }}
              fullWidth
              sx={dateFieldSx}
              InputProps={{
                startAdornment: (
                  <Box
                    sx={{
                      pl: 1.5,
                      pr: 0.5,
                      display: "flex",
                      alignItems: "center",
                      color: COLORS.surface.secondaryText,
                      flexShrink: 0
                    }}
                  >
                    <EventOutlined sx={iconSx} />
                  </Box>
                )
              }}
            />
          </Box>

          <FormRowSwitch label={t["Do not Expire"]} checked={noExpiry} onChange={setNoExpiry} />
        </Stack>

        <FormModalActions
          onCancel={onClose}
          cancelLabel={t["Cancel"]}
          submitLabel={t["Generate"]}
          loading={isLoading}
          disabled={disabledSubmit}
        />
      </Box>
    </MyModal>
  );
}
